/**
 * Standing notice between the admin top bar and the page — the DPA
 * change, stuck handoffs, an impersonated session.
 *
 * Before KAN-404 each of these was hand-assembled from the same copied
 * class list, which cost them the two things a notice is for:
 *
 *   1. **Severity was invisible.** "Your data export is suspended" and
 *      "something is running late" wore the same amber, so nothing told
 *      the reader which one to act on. Tone now carries that: ``danger``
 *      blocks, ``warn`` informs.
 *   2. **The action was the whole strip.** A full-width link has no
 *      affordance — there is nothing to aim at. The action is now a
 *      link on the right with an arrow, and the card around it is inert.
 *
 * ⚠️ Colours come from the purpose tokens (``bg-warn-bg`` +
 * ``border-warn-border``), NEVER from the alpha forms (``bg-warn/10``).
 * The transparent variant puts #f3e9e1 under the text — contrast 4.19,
 * under WCAG AA. These banners live in the shell, so when that regressed
 * it failed a11y on EVERY admin page, not one. The purpose tokens give
 * 4.84. Hover is therefore on the BORDER, not the background: using
 * ``warn-border`` as a surface comes out at 4.03 and would just move the
 * failure into the hover state.
 */

import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";

import { cx } from "@/lib/utils";

/**
 * ``danger`` for blocking — something the reader cannot get past until
 * they act. ``warn`` for informative — worth knowing, not in the way.
 */
export type ShellBannerTone = "warn" | "danger";

/**
 * A link when the action is a destination, a button when it is a
 * mutation (ending an impersonated session is a logout, not a route).
 */
export type ShellBannerAction =
  | { to: string; label: string }
  | { onClick: () => void; label: string; disabled?: boolean };

interface ShellBannerProps {
  tone: ShellBannerTone;
  /** The message. Kept as children so callers own their own markup. */
  children: ReactNode;
  /** Defaults to a warning triangle; pass one to say something else. */
  icon?: ReactNode;
  action?: ShellBannerAction;
  /**
   * Omit for blocking notices. A banner the reader can close is a
   * banner they can close on the one thing they needed to read.
   */
  onDismiss?: () => void;
  /** Accessible name for the close control. Required with ``onDismiss``. */
  dismissLabel?: string;
  /**
   * Goes on the card. The action gets ``<testId>-action`` so a test can
   * reach the link now that the card itself is no longer one.
   */
  testId?: string;
  /** Test id for the action control, when the caller already owns one. */
  actionTestId?: string;
}

const TONE: Record<ShellBannerTone, string> = {
  warn: "border-warn-border bg-warn-bg text-warn",
  danger: "border-danger-border bg-danger-bg text-danger",
};

export function ShellBanner({
  tone,
  children,
  icon,
  action,
  onDismiss,
  dismissLabel,
  testId,
  actionTestId,
}: ShellBannerProps): JSX.Element {
  // The action's classes are the same whether it ends up a link or a
  // button, so the two branches below differ only in the element.
  const actionClass =
    "inline-flex shrink-0 items-center gap-1 self-start font-medium " +
    "underline underline-offset-2 hover:no-underline disabled:opacity-60";
  const arrow = (
    <span aria-hidden="true" className="text-base leading-none">
      →
    </span>
  );

  return (
    // Lines the card up with ``main``'s content column — same max width
    // and same gutters, so a banner does not hang out past the page it
    // is talking about. ``pt-3`` is also the gap when two stack.
    <div className="mx-auto w-full max-w-[1440px] px-4 pt-3 sm:px-6 lg:px-8">
      <div
        data-testid={testId}
        className={cx(
          "flex items-start gap-3 rounded-md border px-4 py-3 text-sm",
          TONE[tone],
        )}
      >
        <span className="mt-0.5 shrink-0">{icon ?? <WarningTriangle />}</span>
        <div className="min-w-0 flex-1">{children}</div>
        {action &&
          ("to" in action ? (
            <Link
              to={action.to}
              className={actionClass}
              data-testid={actionTestId ?? (testId ? `${testId}-action` : undefined)}
            >
              {action.label}
              {arrow}
            </Link>
          ) : (
            <button
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              className={actionClass}
              data-testid={actionTestId ?? (testId ? `${testId}-action` : undefined)}
            >
              {action.label}
              {arrow}
            </button>
          ))}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            // Opacity rather than a hover background: every tinted
            // surface in reach fails contrast under this text (see the
            // note at the top), and a hover state is not worth
            // reopening that.
            className="shrink-0 self-start opacity-70 transition-opacity hover:opacity-100"
            data-testid={testId ? `${testId}-dismiss` : undefined}
          >
            <CloseGlyph />
          </button>
        )}
      </div>
    </div>
  );
}

/** Warning triangle, drawn so it inherits the tone's ``currentColor``. */
function WarningTriangle(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function CloseGlyph(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      className="h-4 w-4"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
